// components/Admin/IntegrationKeys.jsx
// Manage per-facility API keys for external EMR integrations (e.g. Helium Health)
import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Table, TableBody, TableCell, TableContainer, TableHead,
  TableRow, Paper, Button, IconButton, Tooltip, Dialog, DialogTitle,
  DialogContent, DialogActions, TextField, MenuItem, CircularProgress,
  Chip, Alert, Snackbar, InputAdornment,
} from '@mui/material';
import {
  Add as AddIcon,
  DeleteOutline as RevokeIcon,
  ContentCopy as CopyIcon,
  VpnKey as KeyIcon,
  Visibility as ShowIcon,
  VisibilityOff as HideIcon,
  CheckCircle as CopiedIcon,
} from '@mui/icons-material';
import { integrationKeyService } from '../../services/integrationKeys';
import { getFacilities } from '../../services/facilities';

const EXPIRY_OPTIONS = [
  { label: 'No expiry',  value: 0 },
  { label: '30 days',   value: 30 },
  { label: '90 days',   value: 90 },
  { label: '6 months',  value: 180 },
  { label: '1 year',    value: 365 },
];

export default function IntegrationKeys() {
  const [keys, setKeys]           = useState([]);
  const [facilities, setFacilities] = useState([]);
  const [loading, setLoading]     = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm]           = useState({ facilityId: '', facilityName: '', label: '', expiresInDays: 365 });
  const [generating, setGenerating] = useState(false);
  const [newKey, setNewKey]       = useState(null);   // shown once after generation
  const [showKey, setShowKey]     = useState(false);
  const [copied, setCopied]       = useState(false);
  const [snackbar, setSnackbar]   = useState({ open: false, message: '', severity: 'success' });

  const fetchKeys = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await integrationKeyService.list();
      setKeys(Array.isArray(data) ? data : []);
    } catch {
      showSnackbar('Failed to load API keys', 'error');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchKeys();
    getFacilities().then(res => {
      // getFacilities() returns response.data directly: { success, facilities: [...] }
      const list = res?.facilities || (Array.isArray(res) ? res : []);
      setFacilities(list);
    }).catch(() => {});
  }, [fetchKeys]);

  const showSnackbar = (message, severity = 'success') =>
    setSnackbar({ open: true, message, severity });

  const handleFacilityChange = (e) => {
    const fac = facilities.find(f => f._id === e.target.value);
    setForm(prev => ({
      ...prev,
      facilityId: e.target.value,
      facilityName: fac?.facilityName || fac?.name || '',
    }));
  };

  const handleGenerate = async () => {
    if (!form.facilityId || !form.facilityName) {
      showSnackbar('Please select a facility', 'error');
      return;
    }
    setGenerating(true);
    try {
      const { data } = await integrationKeyService.generate({
        facilityId:    form.facilityId,
        facilityName:  form.facilityName,
        label:         form.label || 'Default',
        expiresInDays: form.expiresInDays || undefined,
      });
      setNewKey(data.apiKey);
      setShowKey(true);
      fetchKeys();
    } catch {
      showSnackbar('Failed to generate key', 'error');
    } finally {
      setGenerating(false);
    }
  };

  const handleRevoke = async (id, prefix) => {
    if (!window.confirm(`Revoke key ${prefix}...? Any system using it will immediately lose access.`)) return;
    try {
      await integrationKeyService.revoke(id);
      showSnackbar('API key revoked');
      fetchKeys();
    } catch {
      showSnackbar('Failed to revoke key', 'error');
    }
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(newKey).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    });
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setNewKey(null);
    setShowKey(false);
    setCopied(false);
    setForm({ facilityId: '', facilityName: '', label: '', expiresInDays: 365 });
  };

  const formatDate = (d) => d ? new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';

  return (
    <Box sx={{ maxWidth: 1100, mx: 'auto', px: { xs: 2, sm: 3 }, py: 4 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <KeyIcon sx={{ color: 'primary.main', fontSize: 28 }} />
          <Box>
            <Typography variant="h5" fontWeight={700}>Integration API Keys</Typography>
            <Typography variant="body2" color="text.secondary">
              Issue scoped API keys for external EMR systems (e.g. Helium Health). Each key is facility-locked and rate-limited.
            </Typography>
          </Box>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setDialogOpen(true)}
          sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600 }}
        >
          Generate Key
        </Button>
      </Box>

      {/* Keys table */}
      <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ '& th': { fontWeight: 700, py: 1.5 } }}>
              <TableCell>Key Prefix</TableCell>
              <TableCell>Label</TableCell>
              <TableCell>Facility</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Last Used</TableCell>
              <TableCell>Expires</TableCell>
              <TableCell>Created</TableCell>
              <TableCell align="center">Revoke</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                  <CircularProgress size={24} />
                </TableCell>
              </TableRow>
            ) : keys.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                  No API keys yet. Generate one to connect an external EMR.
                </TableCell>
              </TableRow>
            ) : keys.map((k) => (
              <TableRow key={k._id} hover>
                <TableCell sx={{ fontFamily: 'monospace', fontWeight: 600 }}>
                  {k.keyPrefix}…
                </TableCell>
                <TableCell>{k.label}</TableCell>
                <TableCell>{k.facilityName || '—'}</TableCell>
                <TableCell>
                  <Chip
                    label={k.isActive ? 'Active' : 'Revoked'}
                    color={k.isActive ? 'success' : 'default'}
                    size="small"
                    variant="outlined"
                  />
                </TableCell>
                <TableCell>{formatDate(k.lastUsedAt)}</TableCell>
                <TableCell>{formatDate(k.expiresAt)}</TableCell>
                <TableCell>{formatDate(k.createdAt)}</TableCell>
                <TableCell align="center">
                  {k.isActive && (
                    <Tooltip title="Revoke key">
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleRevoke(k._id, k.keyPrefix)}
                      >
                        <RevokeIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Generate key dialog */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>
          {newKey ? 'Copy Your API Key' : 'Generate Integration API Key'}
        </DialogTitle>
        <DialogContent dividers>
          {newKey ? (
            <Box>
              <Alert severity="warning" sx={{ mb: 2 }}>
                This key will <strong>not be shown again</strong>. Copy it now and store it securely before closing.
              </Alert>
              <TextField
                fullWidth
                label="API Key"
                value={showKey ? newKey : '•'.repeat(newKey.length)}
                InputProps={{
                  readOnly: true,
                  sx: { fontFamily: 'monospace', fontSize: 13 },
                  endAdornment: (
                    <InputAdornment position="end">
                      <Tooltip title={showKey ? 'Hide' : 'Reveal'}>
                        <IconButton onClick={() => setShowKey(p => !p)} size="small">
                          {showKey ? <HideIcon /> : <ShowIcon />}
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={copied ? 'Copied!' : 'Copy to clipboard'}>
                        <IconButton onClick={handleCopy} size="small" color={copied ? 'success' : 'default'}>
                          {copied ? <CopiedIcon /> : <CopyIcon />}
                        </IconButton>
                      </Tooltip>
                    </InputAdornment>
                  ),
                }}
              />
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                Send this key to the integration partner. They must include it as <code>X-API-Key</code> in every request.
              </Typography>
            </Box>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
              <TextField
                select
                fullWidth
                label="Facility"
                value={form.facilityId}
                onChange={handleFacilityChange}
              >
                {facilities.map(f => (
                  <MenuItem key={f._id} value={f._id}>
                    {f.facilityName || f.name}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                fullWidth
                label="Label (e.g. Helium Health integration)"
                value={form.label}
                onChange={e => setForm(p => ({ ...p, label: e.target.value }))}
              />
              <TextField
                select
                fullWidth
                label="Expiry"
                value={form.expiresInDays}
                onChange={e => setForm(p => ({ ...p, expiresInDays: e.target.value }))}
              >
                {EXPIRY_OPTIONS.map(o => (
                  <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
                ))}
              </TextField>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={handleCloseDialog} sx={{ textTransform: 'none' }}>
            {newKey ? 'Done' : 'Cancel'}
          </Button>
          {!newKey && (
            <Button
              variant="contained"
              onClick={handleGenerate}
              disabled={generating || !form.facilityId}
              startIcon={generating ? <CircularProgress size={16} /> : <KeyIcon />}
              sx={{ textTransform: 'none', fontWeight: 600 }}
            >
              Generate Key
            </Button>
          )}
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar(p => ({ ...p, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar(p => ({ ...p, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
